import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./devicealert.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import { Checkbox, FormControlLabel, Typography, Snackbar, Slide, IconButton, AccordionDetails, AccordionSummary, Accordion, CircularProgress } from "@mui/material";
import { Home, Trash2, X } from "react-feather";
import axios from "axios";
import { toast } from "react-smart-toaster";
import moment from "moment";
import Helmet from "react-helmet";
import Loader from "../../../components/Loader";
import Footer from "../../../components/Footer";
import { TableLoader } from "../../../components/LoaderC";
import FilterComponent from "./FilterComponent";
import PubNubChannel from "../components/pubnub/pubnub-channel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";

const ALERTS_PER_BATCH = 20;

// Mirrors the server-side filters so a real-time alert is only shown when it belongs in the current view
const matchesFilters = (item, filterData) => {
  if (!item?.device_id) return false;

  if (filterData.is_archived !== "all" && (item?.is_archived || 0) !== filterData.is_archived) return false;
  if (filterData.property !== "" && item?.property_id !== filterData.property) return false;
  if (filterData.apartment !== "" && item?.apt_id !== filterData.apartment) return false;

  if (filterData.searchKey !== "") {
    const inputVal = filterData.searchKey.toLowerCase();
    return [item?.title, item?.name, item?.location, item?.product_type, item?.property_name, item?.apt_no, item?.apt_text].some(v => v?.toLowerCase().includes(inputVal));
  }

  return true;
};

const SmartDevicesAlerts = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [alertList, setAlertList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [selectedAlertLength, setSelectedAlertLength] = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", is_archived: 0, property: "", apartment: "", });
  const [taskExpanded, setTaskExpanded] = useState("");
  const latestRequest = useRef(0);
  const sentinelRef = useRef(null);

  const ownerId = user?.["https://propertydek.com/user_metadata"]?.ownerID;

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchKey(filterData.searchKey), filterData.searchKey ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filterData.searchKey]);

  const fetchAlerts = useCallback(async (beforeId) => {
    const requestId = ++latestRequest.current;
    const isFirstBatch = !beforeId;

    if (!isFirstBatch) setLoadingMore(true);

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios.get(process.env.REACT_APP_DEVICE_API_URL + "v1/device-alerts/paginated", {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          per_page: ALERTS_PER_BATCH,
          before_id: beforeId || undefined,
          search: searchKey || undefined,
          property_id: filterData.property !== "" ? filterData.property : undefined,
          apt_id: filterData.apartment !== "" ? filterData.apartment : undefined,
          is_archived: filterData.is_archived,
        },
      });

      // Ignore responses from requests that were superseded by newer filters
      if (requestId !== latestRequest.current) return;

      const rows = response.data?.data || [];
      const pagination = response.data?.pagination || {};

      if (isFirstBatch) {
        setAlertList(rows);
        setSelectedAlerts([]);
        setSelectedAlertLength(0);
      } else {
        // A real-time alert may already be in the list, so skip duplicates
        setAlertList(prev => {
          const loadedIds = new Set(prev.map(i => i.id));
          return prev.concat(rows.filter(i => !loadedIds.has(i.id)));
        });
      }
      setTotalCount(pagination.total || 0);
      setHasMore(!!pagination.has_more);
      setNextBeforeId(pagination.next_before_id || null);
      setLoadError(false);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      console.error('[DeviceAlerts] Failed to load device alerts', error);
      setLoadError(true);
      setHasMore(false);
      toast.error(error?.response?.data?.message || 'Unable to load device alerts. Please try again.');
    } finally {
      if (requestId === latestRequest.current) {
        setContentLoading(false);
        setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, searchKey, filterData.property, filterData.apartment, filterData.is_archived]);

  // Start over from the newest alert whenever the filters change
  useEffect(() => {
    fetchAlerts(null);
  }, [fetchAlerts]);

  // Load the next batch when the sentinel below the list scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting)
        fetchAlerts(nextBeforeId);
    }, { rootMargin: "200px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, nextBeforeId, fetchAlerts, contentLoading]);

  const checkboxCng = (e) => {
    let selectedAlertsTemp = selectedAlerts;
    let chkValue = parseInt(e.target.value);
    if (e.target.checked) {
      if (selectedAlertsTemp.indexOf(chkValue) === -1)
        selectedAlertsTemp = selectedAlertsTemp.concat([chkValue]);
    } else {
      let indexOf = selectedAlertsTemp.indexOf(chkValue);
      if (indexOf > -1) selectedAlertsTemp.splice(indexOf, 1);
    }
    setSelectedAlertLength(selectedAlertsTemp.length);
    setSelectedAlerts(selectedAlertsTemp);
  };

  const selectableAlerts = alertList?.filter((i) => i.is_archived === 0);

  // Selects the alerts loaded so far; more load as the user scrolls
  const checkboxAllCng = (e) => {
    let selectedAlertsTemp = [];
    if (e.target.checked) {
      selectedAlertsTemp = selectableAlerts?.map((item) => {
        return parseInt(item?.id);
      });
    }

    setSelectedAlertLength(selectedAlertsTemp.length);
    setSelectedAlerts(selectedAlertsTemp);
  };

  const deleteAlerts = async () => {
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    await axios.post(process.env.REACT_APP_COMMUNICATION_MAIN_API_URL + "v1/smart-devices/smart-devices-alerts-archive", { alertIds: selectedAlerts }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg(selectedAlertLength > 1 ? selectedAlertLength + " alerts archived successfully!" : selectedAlertLength + " alert archived successfully!"
      );
      setSnackbarOpen(true);

      if (filterData.is_archived === "all") {
        // Archived alerts still belong in the "All" view
        setAlertList(prev => prev.map((i) => selectedAlerts.indexOf(parseInt(i.id)) > -1 ? { ...i, is_archived: 1 } : i));
      } else {
        setAlertList(prev => prev.filter((i) => selectedAlerts.indexOf(parseInt(i.id)) === -1));
        setTotalCount(prev => Math.max(prev - selectedAlerts.length, 0));
      }

      setSelectedAlerts([]);
      setSelectedAlertLength(0);
    }).catch((error) => {
      setLoading(false);
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
    });
  };

  const updateDeviceStatus = (deviceId, deviceStatus, bizCode, data) => {
    try {
      if (!data || !deviceId || !matchesFilters(data, filterData) || alertList.some(i => i.id === data.id)) {
        return;
      }

      setAlertList(prev => [data, ...prev]);
      setTotalCount(count => count + 1);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange3 = (panel) => (event, isTaskExpanded) => {
    setTaskExpanded(isTaskExpanded ? panel : false);
  };

  return (<React.Fragment>
    {loading && <Loader />}
    <Helmet title="Device Alert" />

    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Device Alert</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Smart Devices</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Device Alert</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          <div className="control-pane">
            <div className={`${styles.Row}`}>

              <FilterComponent filterData={filterData} setFilterData={setFilterData.bind(this)} />

              {contentLoading && (<div className="Loader TableLoader">
                <TableLoader />
              </div>)}

              {!contentLoading && (<>
                {isAuthenticated && (user["https://propertydek.com/permissions"].indexOf("device-alert:delete") > -1) && <div className={`${styles.AlertMainCheckSec}`}>
                  {selectableAlerts?.length > 0 && (<FormControlLabel
                    className="CustomCheck"
                    control={<Checkbox
                      onChange={checkboxAllCng}
                      indeterminate={
                        selectedAlertLength > 0 &&
                        selectableAlerts?.length !== selectedAlertLength
                      }
                      checked={
                        selectableAlerts?.length > 0 &&
                        selectableAlerts?.length === selectedAlertLength
                      }
                    />}
                    label="Select All Alerts"
                  />)}
                  {selectedAlertLength > 0 && (<button className={`${styles.AlertDeleteBU}`} onClick={deleteAlerts}>
                    <Trash2 /> Delete {selectedAlertLength > 1 ? selectedAlertLength + " Alerts" : selectedAlertLength + " Alert"}
                  </button>)}
                </div>}

                <div className={`${styles.NotesRow}`}>
                  <div className="FormAccording DeviceAlert Theme">
                    {alertList?.map((item) => {
                      return (<Accordion key={item?.id} expanded={taskExpanded === "panel" + item?.id} onChange={handleChange3("panel" + item?.id)}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                          <div className={`${styles.NotesCard}`}>
                            <div className={`${styles.AlertCardFirstSec}`}>
                              {isAuthenticated && (user["https://propertydek.com/permissions"].indexOf("device-alert:delete") > -1) && item?.is_archived === 0 && (<FormControlLabel
                                className="CustomCheck"
                                control={<Checkbox
                                  checked={selectedAlerts?.indexOf(parseInt(item.id)) > -1}
                                  onChange={checkboxCng}
                                  value={item?.id}
                                />}
                                onClick={(e) => e.stopPropagation()}
                              />)}

                              {item?.title === "Water Leak" && (<div className={`${styles.AlertsCardImg} ${styles.Waterleak}`}>
                                <img src="/static/img/water-dash.svg" width="24px" height="24px" alt="" />
                              </div>)}

                              {item?.title === "Low Battery" && (<div className={`${styles.AlertsCardImg} ${styles.Lowbatter}`}>
                                <img src="/static/img/battery-dash.svg" width="24px" height="24px" alt="" />
                              </div>)}

                              {item?.title === "Device Offline" && (<div className={`${styles.AlertsCardImg} ${styles.Offline}`}>
                                <img src="/static/img/wifi-dash.svg" width="24px" height="24px" alt="" />
                              </div>)}

                              {item?.title === "Device Online" && (<div className={`${styles.AlertsCardImg} ${styles.Online}`}>
                                <img src="/static/img/online.svg" width="24px" height="24px" alt="" />
                              </div>)}

                              {(item?.title?.toLowerCase().includes('door unlocked') || item?.title?.toLowerCase().includes('door is unlocked') || item?.title?.toLowerCase().includes('door was unlocked')) && (<div className={`${styles.AlertsCardImg} ${styles.Online}`}>
                                <img src="/static/img/lock-dash.svg" width="24px" height="24px" alt="" />
                              </div>)}

                              <div className={`${styles.AccoTitleHead}`}>
                                <p className={`${styles.AlertTitle}`}>{item?.title}</p>
                                <p className={`${styles.AlertText}`}>{moment(item?.created_at).fromNow()}</p>
                              </div>
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails className={`${styles.CussAccoRow}`}>
                          <div className={`${styles.AccoMainSec}`}>
                            <ul>
                              <li>
                                <p className={`${styles.AlertText}`}>
                                  <span>Device Location:</span>
                                  {item?.product_type}/{item?.location}
                                </p>
                              </li>
                              <li>
                                <p className={`${styles.AlertText}`}>
                                  <span>Device Name:</span>
                                  {item?.name}
                                </p>
                              </li>
                              <li>
                                <p className={`${styles.AlertText}`}>
                                  <span>Property:</span>
                                  {item?.property_name}
                                </p>
                              </li>
                              <li>
                                <p className={`${styles.AlertText}`}>
                                  <span>Unit:</span>
                                  {item?.apt_no ? item?.apt_no : item?.apt_text}
                                </p>
                              </li>
                            </ul>
                          </div>
                        </AccordionDetails>
                      </Accordion>);
                    })}





                  </div>

                  {alertList.length > 0 && (<div ref={sentinelRef} className={`${styles.LoadMoreSec}`}>
                    {loadingMore && <CircularProgress size={24} />}
                    {!hasMore && !loadingMore && totalCount > ALERTS_PER_BATCH && (<p className={`${styles.AlertText}`}>All {totalCount} alerts loaded</p>)}
                  </div>)}

                  {alertList?.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                    <p className={`${styles.NoDataText}`}>Something went wrong</p>
                    <p className={`${styles.NoDataTextSub}`}>We couldn't load device alerts. Please refresh the page to try again.</p>
                  </div>)}

                  {alertList?.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                    <div className={`${styles.NoDataIMG}`}>
                      <img src="/static/img/no-device.png" alt="No Data" />
                    </div>
                    <p className={`${styles.NoDataText}`}>Don't Worry!</p>
                    <p className={`${styles.NoDataTextSub}`}>Device Alerts is empty.</p>
                  </div>)}

                </div>
              </>)}




            </div>
          </div>
        </div>
      </div>
    </div>

    {ownerId && (<PubNubChannel ownerId={ownerId} updateDeviceStatus={updateDeviceStatus}></PubNubChannel>)}

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </React.Fragment>);
};

export default withCustomAuthenticationRequired(SmartDevicesAlerts, {
  onRedirecting: () => <Loader />,
});